import React from 'react';
import { useConsultation } from '../contexts/ConsultationContext';
import DrawerShell from './DrawerShell';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { FileText, Thermometer, Heart, Activity, Stethoscope } from 'lucide-react';

const TEMPLATE_ICONS = {
  fever: Thermometer,
  hypertension: Heart,
  diabetes: Activity,
  routine: Stethoscope,
};

export function TemplateSelector({ open, onOpenChange, onSelect }) {
  const { templates } = useConsultation();

  const handleSelect = (template) => {
    onSelect(template);
    onOpenChange(false);
  };

  const groupedTemplates = templates.reduce((acc, template) => {
    const category = template.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(template);
    return acc;
  }, {});

  return (
    <DrawerShell
      open={open}
      onClose={() => onOpenChange(false)}
      eyebrow="Insert Template"
      title="Clinical Templates"
      subtitle="Populate consultation fields in one click"
      accent="#1d4ed8"
      icon={<FileText className="h-5 w-5" />}
      width="md"
      testId="template-selector"
    >
      <div className="p-5">
        <div className="text-xs text-zinc-500 mb-4 rounded-lg bg-blue-50/60 border border-blue-100 p-2.5">
          Tip — type <kbd className="kbd">/</kbd> followed by a template name inside any editor to insert quickly.
        </div>

        <div className="space-y-5">
          {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => {
            const Icon = TEMPLATE_ICONS[category] || FileText;
            return (
              <div key={category}>
                <h3 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <Icon className="h-3 w-3" />
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </h3>
                <div className="space-y-2">
                  {categoryTemplates.map((template) => (
                    <Button
                      key={template.id}
                      variant="outline"
                      className="w-full justify-start h-auto py-3 px-4 bg-white hover:bg-blue-50 hover:border-blue-300"
                      onClick={() => handleSelect(template)}
                      data-testid={`template-${template.name.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <div className="text-left flex-1">
                        <p className="font-bold text-zinc-900">{template.name}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {template.content?.chief_complaints && (
                            <Badge variant="secondary" className="text-xs">Complaints</Badge>
                          )}
                          {template.content?.clinical_examination && (
                            <Badge variant="secondary" className="text-xs">Examination</Badge>
                          )}
                          {template.content?.investigations && (
                            <Badge variant="secondary" className="text-xs">
                              {template.content.investigations.length} tests
                            </Badge>
                          )}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            );
          })}

          {templates.length === 0 && (
            <div className="text-center py-10 text-zinc-400">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No templates available</p>
            </div>
          )}
        </div>
      </div>
    </DrawerShell>
  );
}
